import { useState } from 'react';
import { useAuth } from '../../shared/auth/use-auth';
import { cn } from '../../shared/lib/cn';
import { getButtonClassNames } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Switch } from '../../shared/ui/Switch/Switch';
import { Stack } from '../../shared/ui/Stack';
import { StaffMarkdown } from '../../shared/ui/markdown/StaffMarkdown';
import { acceptAgreement, type OnboardingResponse } from '../../entities/me/me.api';

type Props = {
  onboarding: OnboardingResponse;
  onSuccess: () => void;
};

function nextStep(ob: OnboardingResponse): 'pd' | 'crm' | 'org' | 'done' {
  if (ob.need_personal_data_agreement) return 'pd';
  if (ob.need_crm_agreement) return 'crm';
  if (ob.need_org_agreement) return 'org';
  return 'done';
}

export default function AgreementsGatePage({ onboarding, onSuccess }: Props) {
  const { apiClient, logout } = useAuth();
  const [currentOnboarding, setCurrentOnboarding] = useState<OnboardingResponse>(onboarding);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = nextStep(currentOnboarding);

  let title = '';
  let description = '';
  let body = '';
  let agreementId = 0;
  let version = 0;
  let stepText = '';

  if (step === 'pd') {
    title = 'Политика конфиденциальности';
    description = 'Согласие на обработку персональных данных';
    body = currentOnboarding.personal_data_agreement_text;
    agreementId = currentOnboarding.personal_data_agreement_id ?? 0;
    version = currentOnboarding.personal_data_agreement_version;
    stepText = 'Шаг 1 из 3';
  } else if (step === 'crm') {
    title = 'Оферта CRM';
    description = 'Пользовательское соглашение с платформой CRM';
    body = currentOnboarding.crm_agreement_text;
    agreementId = currentOnboarding.crm_agreement_id ?? 0;
    version = currentOnboarding.crm_agreement_version;
    stepText = 'Шаг 2 из 3';
  } else if (step === 'org') {
    title = 'Пользовательское соглашение';
    description = 'Правила для пользователей организации и их права';
    body = currentOnboarding.org_agreement_text;
    agreementId = currentOnboarding.org_agreement_id ?? 0;
    version = currentOnboarding.org_agreement_version;
    stepText = 'Шаг 3 из 3';
  }

  const handleAccept = async () => {
    if (!checked || loading) return;
    setLoading(true);
    setError(null);
    try {
      await acceptAgreement(apiClient, agreementId, {
        accepted_version: version,
        organization_id: step === 'org' ? (currentOnboarding.org_agreement_id ? undefined : undefined) : undefined
      });

      // Update local state to simulate acceptance
      const nextOnboarding = { ...currentOnboarding };
      if (step === 'pd') {
        nextOnboarding.need_personal_data_agreement = false;
      } else if (step === 'crm') {
        nextOnboarding.need_crm_agreement = false;
      } else if (step === 'org') {
        nextOnboarding.need_org_agreement = false;
      }

      setChecked(false);
      setCurrentOnboarding(nextOnboarding);

      if (nextStep(nextOnboarding) === 'done') {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось принять соглашение');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="staff-system-shell flex items-center justify-center min-h-screen bg-[hsl(var(--ui-background))] p-4" data-testid="agreements-gate-page">
      <Card className="w-full max-w-2xl shadow-2xl border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] overflow-hidden flex flex-col max-h-[90vh]">
        <CardHeader className="border-b border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted))]/30 pb-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--ui-accent))]" data-testid="agreements-step-counter">
              {stepText}
            </span>
            <button
              onClick={logout}
              className="text-xs text-[hsl(var(--ui-muted-foreground))] hover:text-[hsl(var(--ui-foreground))] transition-colors"
            >
              Выйти из аккаунта
            </button>
          </div>
          <CardTitle className="text-xl font-bold mt-1 text-[hsl(var(--ui-foreground))]">{title}</CardTitle>
          <CardDescription className="text-sm text-[hsl(var(--ui-muted-foreground))]">{description}</CardDescription>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6 max-h-[50vh] border-b border-[hsl(var(--ui-border))]">
          <div className="prose prose-sm dark:prose-invert max-w-none text-[hsl(var(--ui-foreground))]">
            <StaffMarkdown content={body} />
          </div>
        </CardContent>

        <div className="p-6 bg-[hsl(var(--ui-muted))]/10 flex flex-col gap-4 shrink-0">
          {error && (
            <div className="p-3 text-sm text-[hsl(var(--ui-destructive))] bg-[hsl(var(--ui-destructive))]/10 rounded-lg border border-[hsl(var(--ui-destructive))]/20">
              {error}
            </div>
          )}

          <div className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))]">
            <Switch
              checked={checked}
              onCheckedChange={setChecked}
              className="mt-0.5"
              id="agree-switch"
              data-testid="agreements-switch"
            />
            <label
              htmlFor="agree-switch"
              className="text-sm leading-snug text-[hsl(var(--ui-foreground))] cursor-pointer select-none"
            >
              Я подтверждаю, что ознакомился(ась) с текстом и в полном объеме принимаю все условия данного соглашения.
            </label>
          </div>

          <Stack direction="row" gap={3} className="justify-end mt-2">
            <button
              data-testid="agreements-cancel"
              onClick={logout}
              className={cn(getButtonClassNames({ variant: 'secondary' }), 'px-5')}
            >
              Отмена
            </button>
            <button
              data-testid="agreements-accept"
              disabled={!checked || loading}
              onClick={handleAccept}
              className={cn(
                getButtonClassNames({ variant: 'primary' }),
                'px-6',
                (!checked || loading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {loading ? 'Принятие...' : 'Принять и продолжить'}
            </button>
          </Stack>
        </div>
      </Card>
    </div>
  );
}
